/**
 * Экран SKOKA Pro — один на все места, где человек упирается в Pro
 * (макет docs/design/pro-2026-10-21, решения владельца 21.10.2026).
 *
 * Меняются только заголовок и главный пример — то, что человек хотел сделать:
 * сказал голосом, снял чек, загрузил выписку, спросил, открыл Аналитику. Ниже —
 * польза словами человека и «Без риска», внизу — покупка. Пробного на этом
 * экране нет: он предлагается один раз после отказа (`TrialOffer`).
 *
 * Пока приём оплаты не подключён, вместо покупки — пробный период, если он
 * ещё не был: продавать то, что нельзя купить, нельзя.
 */
import { useState } from 'react';
import { Icon } from './icon.js';
import { Wordmark } from './logo.js';
import { proPeriodEnd } from './pro-period.js';
import type { AccessState } from './access.js';

export type ProEntry = 'voice' | 'scan' | 'file' | 'balances' | 'ask' | 'insight' | 'analytics' | 'review' | 'general';

const ASK_ICON = 'messages-question';

export function ProLockup({ size }: { size?: 'm' | 'xl' }) {
  return (
    <span className={`pro-lockup${size ? ` ${size}` : ''}`}>
      <Wordmark /><b>PRO</b>
    </span>
  );
}

/* ── Живые примеры: что получит человек, а не перечень функций ─────────── */

function OpRow({ icon, color, name, sub, amount }: { icon: string; color: string; name: string; sub: string; amount: string }) {
  return (
    <div className="demo-op">
      <span className={`catico ${color}`}><Icon name={icon} size={19} /></span>
      <div className="nm"><b>{name}</b><span>{sub}</span></div>
      <b className="am num">{amount}</b>
    </div>
  );
}

const VoiceDemo = () => (
  <div className="demo">
    <div className="demo-say">
      <span className="demo-mic"><Icon name="microphone" size={15} /></span>
      <span className="demo-wave" aria-hidden="true">{Array.from({ length: 9 }, (_, i) => <i key={i} />)}</span>
      <span className="demo-text">такси домой 640 с Т-Банка</span>
    </div>
    <div className="demo-arrow"><span>3 секунды — и записано</span></div>
    <OpRow icon="taxi" color="c-amber" name="Такси" sub="Т-Банк · сегодня, 21:40" amount="−640 ₽" />
  </div>
);

const ReceiptDemo = () => (
  <div className="demo">
    <div className="demo-say">
      <span className="rc-thumb" aria-hidden="true"><i /><i /><i /><i /><i /></span>
      <span className="rc-cap"><b>Пятёрочка</b><span>снимок чека · 14 позиций · 2 318 ₽</span></span>
    </div>
    <div className="demo-arrow"><span>одним снимком — по категориям</span></div>
    <OpRow icon="cart-shopping" color="c-green" name="Продукты" sub="12 позиций · Пятёрочка" amount="−1 940 ₽" />
    <OpRow icon="spray-can-sparkles" color="c-violet" name="Бытовая химия" sub="2 позиции · Пятёрочка" amount="−378 ₽" />
  </div>
);

const BankDemo = () => (
  <div className="demo">
    <div className="demo-say">
      <span className="bk-thumb" aria-hidden="true"><i /><i /><i /><i /><i /><i /></span>
      <span className="rc-cap"><b>Скриншот из банка</b><span>операции за неделю</span></span>
    </div>
    <div className="demo-arrow"><span>23 операции за раз</span></div>
    <OpRow icon="cart-shopping" color="c-green" name="Продукты" sub="Пятёрочка · 19 октября" amount="−1 240 ₽" />
    <OpRow icon="taxi" color="c-amber" name="Такси" sub="Яндекс Go · 18 октября" amount="−640 ₽" />
    <span className="demo-more">и ещё 21 — с категориями и счётом</span>
  </div>
);

/** Остатки разом: тот же вид, что выписка, — скриншот главного экрана и «было → стало». */
const BalancesDemo = () => (
  <div className="demo">
    <div className="demo-say">
      <span className="bk-thumb" aria-hidden="true"><i /><i /><i /><i /><i /><i /></span>
      <span className="rc-cap"><b>Скриншот из банка</b><span>главный экран со счетами</span></span>
    </div>
    <div className="demo-arrow"><span>все остатки за раз</span></div>
    <OpRow icon="credit-card" color="c-blue" name="Основная карта" sub="было 126 160 ₽" amount="98 400 ₽" />
    <OpRow icon="house" color="c-violet" name="Ипотека" sub="долг был 3 210 000 ₽" amount="3 180 500 ₽" />
    <span className="demo-more">разница — в корректировку, не в траты</span>
  </div>
);

const ChatDemo = () => (
  <div className="demo">
    <div className="demo-q">Хватит на отпуск в июле — 120 000 ₽?</div>
    <div className="demo-a">
      <span className="demo-ava"><Icon name={ASK_ICON} size={14} /></span>
      <p>Хватит, если с ноября откладывать по <b>22 500 ₽</b>. Сейчас уходит 18 000 — не хватает 4 500.
        Проще всего взять из кафе: в октябре на них ушло <b>14 200 ₽</b>.</p>
    </div>
  </div>
);

/** Вопрос уже вписан словами и суммой человека — ответ откроется в Pro. */
const QuestionDemo = ({ question }: { question: string }) => (
  <div className="demo">
    <div className="demo-q">{question}</div>
    <div className="demo-a">
      <span className="demo-ava"><Icon name={ASK_ICON} size={14} /></span>
      <p className="skel" aria-hidden="true"><i /><i /><i /><i className="s" /></p>
    </div>
    <span className="demo-more">Ответ — с твоими счетами, платежами и датами</span>
  </div>
);

function Donut() {
  const parts: [number, string][] = [[38, '#E8368F'], [24, '#1E88E5'], [16, '#C98A00'], [12, '#17A34A'], [10, '#7C4DFF']];
  const C = 2 * Math.PI * 52;
  let acc = 0;
  return (
    <svg viewBox="0 0 120 120" aria-hidden="true"><g transform="rotate(-90 60 60)">
      {parts.map(([pct, col]) => {
        const len = (C * pct) / 100;
        const d = len - 2.5;
        const off = -acc;
        acc += len;
        return <circle key={col} cx="60" cy="60" r="52" fill="none" stroke={col} strokeWidth="14"
          strokeDasharray={`${d} ${C - d}`} strokeDashoffset={off} />;
      })}
    </g></svg>
  );
}

const AnalyticsDemo = () => (
  <div className="demo an-demo">
    <div className="an-mini">
      <div className="an-mini-d"><Donut /><span className="num">62 400 ₽</span></div>
      <div className="an-mini-l">
        <span><i style={{ background: '#E8368F' }} />Кафе 38%</span>
        <span><i style={{ background: '#1E88E5' }} />Продукты 24%</span>
        <span><i style={{ background: '#C98A00' }} />Транспорт 16%</span>
      </div>
    </div>
    <div className="demo-a">
      <span className="demo-ava"><Icon name="sparkles" size={13} /></span>
      <p><b>Кафе — 14 200 ₽</b>, на 40% больше обычного, почти всё в выходные. Самый дорогой день — суббота.</p>
    </div>
  </div>
);

const ReviewDemo = () => (
  <div className="demo">
    <div className="rv-h"><b>Прошлый период</b><span>пример</span></div>
    <p className="rv-p">Кафе — <b>14 200 ₽</b> вместо обычных 9 000. Вернёшь к обычному — «Отпуск» соберётся <b>на месяц раньше</b>.</p>
    <div className="rv-b"><span className="on">Попробую</span><span>Не про меня</span></div>
  </div>
);

function Tile({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return <div className="ptile"><span className="ptile-i"><Icon name={icon} size={15} /></span><b>{title}</b>{children}</div>;
}

const TILES: Record<string, React.ReactNode> = {
  voice: <Tile key="voice" icon="microphone" title="Голосом"><span className="ptile-q">«кофе 250»</span><span className="ptile-sub">Сумма, категория и счёт — готово</span></Tile>,
  scan: <Tile key="scan" icon="receipt" title="Чек — по категориям"><span className="ptile-sub">Пятёрочка · 14 позиций</span>
    <span className="ptile-pills"><i>Продукты 1 940 ₽</i><i>Химия 378 ₽</i></span></Tile>,
  file: <Tile key="file" icon="file-lines" title="Выписка из банка"><span className="ptile-sub">Скриншот операций — 23 записи за раз</span></Tile>,
  ask: <Tile key="ask" icon={ASK_ICON} title="Спроси SKOKA"><span className="ptile-q">«Успею на отпуск?»</span>
    <span className="ptile-sub">Ответ с твоими цифрами, а не общие советы</span></Tile>,
  review: <Tile key="review" icon="chart-pie" title="Разбор периода"><span className="ptile-sub">Что выросло и сколько это даст целям</span></Tile>,
};

interface Hero { head: React.ReactNode; sub: React.ReactNode; demo: React.ReactNode; tiles: string[] }

function hero(entry: ProEntry, mine: string | null, question: string | null): Hero {
  const own = mine ? <>{mine}</> : null;
  switch (entry) {
    case 'scan': return {
      head: <>Снял чек —<br /><em>всё разложено.</em></>,
      sub: own ?? 'Снимок чека — и позиции уже по категориям: продукты отдельно, химия отдельно.',
      demo: <ReceiptDemo />, tiles: ['file', 'voice', 'ask'],
    };
    case 'file': return {
      head: <>Выписка —<br /><em>за один раз.</em></>,
      sub: 'Скриншот операций из приложения банка — и они уже здесь, с категориями и счётом.',
      demo: <BankDemo />, tiles: ['scan', 'voice', 'ask'],
    };
    case 'balances': return {
      head: <>Остатки —<br /><em>одним скриншотом.</em></>,
      sub: 'Снимешь экран банка — и все счета, кредитки и кредиты сверены. Разница в траты не попадёт.',
      demo: <BalancesDemo />, tiles: ['file', 'scan', 'voice'],
    };
    case 'ask': return {
      head: <>Спроси.<br /><em>SKOKA посчитает.</em></>,
      sub: 'Ответ про твои деньги — с суммами и датами, а не общие советы.',
      demo: <ChatDemo />, tiles: ['voice', 'scan', 'review'],
    };
    case 'insight': return {
      head: <>Спроси —<br /><em>SKOKA посчитает.</em></>,
      sub: 'Вопрос уже вписан. Ответ — по твоим деньгам, а не общие советы.',
      demo: question ? <QuestionDemo question={question} /> : <ChatDemo />, tiles: ['voice', 'scan', 'review'],
    };
    case 'analytics': return {
      head: <>Видно, куда<br /><em>уходят деньги.</em></>,
      sub: own ?? 'SKOKA найдёт главное в твоих тратах и скажет простыми словами.',
      demo: <AnalyticsDemo />, tiles: ['review', 'ask', 'voice'],
    };
    case 'review': return {
      head: <>Каждый период —<br /><em>ближе к цели.</em></>,
      sub: 'После каждого периода — одно наблюдение про свободные траты и что оно даст целям.',
      demo: <ReviewDemo />, tiles: ['ask', 'voice', 'scan'],
    };
    default: return {
      head: <>Говори.<br />Снимай.<br /><em>Спрашивай.</em></>,
      sub: own ?? 'Pro записывает деньги за тебя и отвечает на вопросы о них.',
      demo: <VoiceDemo />, tiles: ['scan', 'ask', 'review'],
    };
  }
}

function Benefit({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="pben"><span className="pben-i"><Icon name={icon} size={16} /></span>
      <div><b>{title}</b><span>{text}</span></div></div>
  );
}

const Benefits = () => (
  <>
    <div className="pro-sec">Что даёт Pro</div>
    <Benefit icon="clock" title="Не тратить вечера на ввод"
      text="Голосом — 3 секунды на операцию. Чек — одним снимком, выписка из банка — десятки операций за раз." />
    <Benefit icon="chart-pie" title="Видеть, куда уходят деньги"
      text="В Аналитике — простыми словами: что выросло, какой день был необычным, чем период отличается от обычного." />
    <Benefit icon={ASK_ICON} title="Спросить, прежде чем решить"
      text="«Хватит на отпуск?», «Закрыть кредитку или копить?» — ответ с твоими суммами и датами." />
    <Benefit icon="bullseye-arrow" title="Успеть к цели"
      text="После каждого периода — одно наблюдение и сколько оно даст целям. Согласился — в следующий раз видно, получилось ли." />
    <div className="pro-sec">Без риска</div>
    <ul className="pguar">
      {['Автопродление выключено — продлится, только если включишь сам',
        'Передумал — вернём деньги за неиспользованные дни, прямо в приложении',
        'Учёт, «Можно тратить», платежи и цели бесплатны всегда',
        'Все записи остаются твоими — и на Free тоже'].map((t) => (
        <li key={t}><Icon name="check" size={13} /><span>{t}</span></li>
      ))}
    </ul>
  </>
);

export function ProScreen({ entry, access, mine = null, question = null, billing, busy = false, error = null,
  onClose, onBuy, onTrial, onPlans, onTerms }: {
  entry: ProEntry;
  access: AccessState;
  /** Его собственная цифра: «В октябре ты записал вручную 47 операций…». */
  mine?: string | null;
  /** Вписанный вопрос — когда пришёл из «Спросить SKOKA» в карточке. */
  question?: string | null;
  /** Принимается ли оплата. Нет — вместо покупки пробный период. */
  billing: boolean;
  busy?: boolean;
  error?: string | null;
  /** Закрыл или «Не сейчас»: родитель решает, показать ли лист пробного. */
  onClose: () => void;
  onBuy: (period: 1 | 3 | 6 | 12) => void;
  onTrial: () => void;
  onPlans: () => void;
  /** Оферта Pro и правила возврата. */
  onTerms: () => void;
}) {
  const [period, setPeriod] = useState<1 | 3 | 6 | 12>(12);
  const h = hero(entry, mine, question);
  const early = access.earlyYear !== null;
  const yearPrice = access.earlyYear ?? 2990;
  const [scrolled, setScrolled] = useState(false);
  return (
    <div className="pro-screen" role="dialog" aria-modal="true" aria-label="SKOKA Pro">
      <div className="pro-glow" />
      <div className="pro-top"><button className="pro-x" aria-label="Закрыть" onClick={onClose}><Icon name="xmark" size={15} /></button></div>
      <div className="pro-body" onScroll={(e) => { if (!scrolled && e.currentTarget.scrollTop > 40) setScrolled(true); }}>
        <ProLockup />
        <h1 className="pro-h m">{h.head}</h1>
        <p className="pro-sub">{h.sub}</p>
        {/* Ранний доступ — наверху, а не в нижней плашке: она и так высокая. */}
        {early && (
          <div className="pro-early-top">Ранний доступ: первый год — {money(yearPrice)} вместо 2 990 ₽, если оплатить до 31 декабря 2026</div>
        )}
        {h.demo}
        <div className="ptiles">{h.tiles.map((t) => TILES[t])}</div>
        <Benefits />
      </div>
      <div className="pro-cta">
        {/*
          Что экран листается, иначе не догадаться: цены закреплены снизу и
          закрывают низ прокрутки. Подсказка уходит, как только начали листать.
        */}
        {!scrolled && <span className="pro-more" aria-hidden="true">Что даёт Pro <Icon name="chevron-down" size={11} /></span>}
        {error && <span className="pro-fine" role="alert">{error}</span>}
        {/*
          Цены и сроки видны всегда и закреплены снизу (решение владельца
          22.10.2026): год — крупно, цена в месяц главной цифрой, за год —
          цифрой поменьше; ниже полгода, три месяца и месяц. Их смотрит и
          проверка ЮKassa — до того, как оплата включена.
        */}
        <div className="pk-grid" role="radiogroup" aria-label="Срок">
          <button className={`pk pk-year${period === 12 ? ' on' : ''}`} role="radio" aria-checked={period === 12} onClick={() => setPeriod(12)}>
            <span className="pk-r" /><b>Год</b>
            <span className="pk-price"><b className="num">{money(Math.round(yearPrice / 12))}</b> в мес</span>
            <span className="pk-total num">{early ? <><s>2 990 ₽</s> {money(yearPrice)} за первый год</> : `${money(yearPrice)} за год`}</span>
            <i>{early ? '−50% · ранний доступ' : '−38%'}</i>
          </button>
          {PLANS.map((p) => (
            <button key={p.m} className={`pk pk-sm${period === p.m ? ' on' : ''}`} role="radio" aria-checked={period === p.m} onClick={() => setPeriod(p.m)}>
              <span className="pk-head"><span className="pk-r" /><b>{p.m === 1 ? 'Месяц' : `${p.m} мес`}</b></span>
              <span className="pk-price num">{money(p.total)}</span>
              <span className="pk-total">{p.m === 1 ? 'каждый месяц' : p.per.replace(' в мес', ' ₽ в мес')}</span>
            </button>
          ))}
        </div>
        {billing ? (
          <button className="pro-btn" disabled={busy} onClick={() => onBuy(period)}>
            {/* Коротко: за что именно — видно в выбранной карточке над кнопкой. */}
            {period === 12
              ? `Подключить Pro за ${money(yearPrice)}`
              : period === 1 ? 'Подключить Pro за 399 ₽ в месяц'
                : `Подключить Pro за ${money(PLANS.find((p) => p.m === period)!.total)}`}
          </button>
        ) : access.trial.available ? (
          <button className="pro-btn" disabled={busy} onClick={onTrial}>Попробовать {access.trial.days} дней бесплатно</button>
        ) : (
          <button className="pro-btn" disabled>Оплата Pro — совсем скоро</button>
        )}
        <span className="pro-fine">
          {billing ? 'Без автопродления' : access.trial.available ? 'Пробный — без карты' : 'Оплата откроется совсем скоро'}
          {' · '}<button className="lnk-in" onClick={onPlans}>Все сроки</button>
          {' · '}<button className="lnk-in" onClick={onTerms}>Условия и возврат</button>
        </span>
        {/* «Не сейчас» — крупно и отдельно: мелкой ссылкой его не находили. */}
        <button className="pro-later" onClick={onClose}>Не сейчас</button>
      </div>
    </div>
  );
}

/**
 * «Не готов платить? Неделя — бесплатно» — один раз на аккаунт, сразу после
 * отказа от покупки. «Нет, спасибо» — и больше не показываем.
 */
export function TrialOffer({ days, busy, onTrial, onNo }: { days: number; busy: boolean; onTrial: () => void; onNo: () => void }) {
  return (
    <div className="sheet">
      <div className="bg" onClick={onNo} />
      <div className="body pro-sheet" role="dialog" aria-modal="true" aria-label="Пробный период">
        <div className="grab" />
        <ProLockup />
        <h2 className="pro-h s">Не готов платить?<br /><em>Неделя — бесплатно</em></h2>
        <p className="pro-sub">{days} дней Pro целиком: голосом, чеки, выписки и вопросы SKOKA.</p>
        <div className="pro-chips l">
          <span><Icon name="microphone" size={13} /> 50 записей</span>
          <span><Icon name="receipt" size={13} /> 7 чеков</span>
          <span><Icon name={ASK_ICON} size={13} /> 5 вопросов</span>
        </div>
        <button className="pro-btn" disabled={busy} onClick={onTrial}>Попробовать {days} дней бесплатно</button>
        <span className="pro-fine">Без карты · само не продлится · один раз</span>
        <button className="pro-no" onClick={onNo}>Нет, спасибо</button>
      </div>
    </div>
  );
}

/** «7 дней с Pro» — один раз, при первом входе после конца пробного. */
export function TrialEnded({ stats, billing, earlyYear, onBuy, onFree }: {
  stats: { voice: number; receipts: number; answers: number };
  billing: boolean;
  earlyYear: number | null;
  onBuy: () => void;
  onFree: () => void;
}) {
  return (
    <div className="pro-screen" role="dialog" aria-modal="true" aria-label="Пробный период закончился">
      <div className="pro-glow" />
      <div className="pro-top"><button className="pro-x" aria-label="Закрыть" onClick={onFree}><Icon name="xmark" size={15} /></button></div>
      <div className="pro-body">
        <ProLockup />
        <h1 className="pro-h">7 дней<br /><em>с Pro</em></h1>
        <div className="pstats">
          <div className="pstat"><b className="num">{stats.voice}</b><span>записей голосом и словами</span>
            <small>{stats.voice > 0 ? `≈ ${Math.max(1, Math.round((stats.voice * 23) / 60))} мин не ушло на ввод` : 'попробуй в Pro: «кофе 250»'}</small></div>
          <div className="pstat"><b className="num">{stats.receipts}</b><span>чеков и выписок</span>
            <small>разложены по категориям сами</small></div>
          <div className="pstat"><b className="num">{stats.answers}</b><span>ответов SKOKA</span>
            <small>с твоими суммами и датами</small></div>
        </div>
        <p className="pro-sub">С сегодняшнего дня — снова вручную. Всё, что записал, остаётся.</p>
      </div>
      <div className="pro-cta">
        {billing && <button className="pro-btn" onClick={onBuy}>
          {earlyYear ? `Остаться в Pro — ${money(earlyYear)} за первый год` : 'Остаться в Pro — от 249 ₽ в месяц'}</button>}
        <button className="pro-ghost" onClick={onFree}>Вернуться на Free</button>
      </div>
    </div>
  );
}

const money = (n: number) => `${n.toLocaleString('ru-RU').replace(/ /g, ' ')} ₽`;

const PLANS: { m: 1 | 3 | 6 | 12; total: number; per: string; save: string }[] = [
  { m: 6, total: 1790, per: '298 в мес', save: '−25%' },
  { m: 3, total: 990, per: '330 в мес', save: '−17%' },
  { m: 1, total: 399, per: 'каждый месяц', save: '' },
];

/**
 * Все сроки: год крупно и первым, остальные рядом. Автопродление выключено,
 * пока человек не включит сам; под переключателем сказано, что будет.
 */
export function ProPlans({ access, busy, error, onClose, onBuy, onTerms, initial = 12 }: {
  access: AccessState; busy: boolean; error: string | null;
  /** Какой срок выбран сразу: при повторе после отказа банка — тот же, что выбирал. */
  initial?: 1 | 3 | 6 | 12;
  onClose: () => void; onBuy: (months: 1 | 3 | 6 | 12, renew: boolean) => void;
  /** Оферта Pro и правила возврата — в «Условиях». */
  onTerms: () => void;
}) {
  const [months, setMonths] = useState<1 | 3 | 6 | 12>(initial);
  const [renew, setRenew] = useState(false);
  const early = access.earlyYear;
  const total = months === 12 ? (early ?? 2990) : PLANS.find((p) => p.m === months)!.total;
  const until = proPeriodEnd(access, months);
  const untilText = until.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).replace(' г.', '');
  const next = months === 12 ? 2990 : total;
  return (
    <div className="pro-screen" role="dialog" aria-modal="true" aria-label="Сроки SKOKA Pro">
      <div className="pro-glow" />
      <div className="pro-top"><button className="pro-x" aria-label="Закрыть" onClick={onClose}><Icon name="xmark" size={15} /></button></div>
      <div className="pro-body">
        <ProLockup />
        <h1 className="pro-h s">Выбери срок</h1>
        <button className={`pplan best${months === 12 ? '' : ' off'}`} onClick={() => setMonths(12)} aria-pressed={months === 12}>
          <span className="pplan-l"><b>12 месяцев</b>
            <span>{early ? <><s>2 990 ₽</s> {money(early)} за первый год</> : '2 990 ₽ за год · −38%'}</span>
            <i className="pplan-badge">{early ? 'новым пользователям · −50%' : 'как одна чашка кофе в месяц'}</i></span>
          <span className="pplan-r"><b className="num">{money(Math.round((early ?? 2990) / 12))}</b><span>в месяц</span></span>
        </button>
        {early && <div className="pro-early">Скидка новым пользователям — если оплатить до 31 декабря 2026.
          Второй год — 2 990 ₽, предупредим за 7 дней.</div>}
        <div className="pplans3">
          {PLANS.map((p) => (
            <button key={p.m} className={`pplan sm${months === p.m ? ' on' : ''}`} onClick={() => setMonths(p.m)} aria-pressed={months === p.m}>
              <b>{p.m} мес</b><span className="num">{money(p.total)}</span><small>{p.per}</small>
            </button>
          ))}
        </div>
        <label className="pro-sw">
          <span><b>Продлевать автоматически</b>
            <small>{renew
              ? `${money(next)} спишется ${untilText}, напомним за ${months === 12 ? '7 и 3 дня' : '3 дня'}. Отключить — в профиле`
              : `Выключено — ${untilText} Pro закончится, ничего не спишется`}</small></span>
          <input type="checkbox" role="switch" checked={renew} onChange={(e) => setRenew(e.target.checked)} />
          <i className={renew ? 'on' : ''} />
        </label>
        <div className="pro-inc"><div className="pro-inc-h">Каждый месяц подписки</div>
          <div className="pro-inc-r"><Icon name="microphone" size={14} /><span>Голосом и словами</span><b>200</b></div>
          <div className="pro-inc-r"><Icon name="receipt" size={14} /><span>Чеки и выписки</span><b>40</b></div>
          <div className="pro-inc-r"><Icon name={ASK_ICON} size={14} /><span>Вопросы SKOKA</span><b>30</b></div>
          <div className="pro-inc-r"><Icon name="chart-pie" size={14} /><span>Разбор периода</span><b>каждый</b></div>
          <div className="pro-inc-n">Обновляются в число оплаты каждый месяц, и у годовой тоже. Ручная запись,
            «Можно тратить», платежи и цели бесплатны всегда.</div>
        </div>
      </div>
      <div className="pro-cta">
        {error && <span className="pro-fine" role="alert">{error}</span>}
        {access.billing
          ? <button className="pro-btn" disabled={busy} onClick={() => onBuy(months, renew)}>Оплатить {money(total)}</button>
          : <button className="pro-btn" disabled>Оплата Pro — совсем скоро</button>}
        <span className="pro-fine">Картой, SberPay или СБП · чек на почту. Оплачивая, принимаешь{' '}
          <button className="lnk-in" onClick={onTerms}>условия Pro</button> — там и возврат</span>
      </div>
    </div>
  );
}

/** «Ты в Pro» — после подтверждённой оплаты. Знак SKOKA целиком, без отдельных лучей. */
export function ProPaid({ until, next, offer = null, onDone }: {
  until: string | null;
  /** Куда вернуться: к тому, ради чего пришёл, — «Записать голосом», «Снять чек»… */
  next: string;
  /**
   * Автопродление включено — предложить добавить следующее списание в платежи
   * (04.11.2026). По умолчанию включено: приложение само знает о списании.
   */
  offer?: { amount: number; months: number; next: string } | null;
  onDone: (addToPayments: boolean) => void;
}) {
  const [add, setAdd] = useState(true);
  const every = offer ? (offer.months === 12 ? 'раз в год' : offer.months === 1 ? 'каждый месяц' : `раз в ${offer.months} мес.`) : '';
  return (
    <div className="pro-screen" role="dialog" aria-modal="true" aria-label="Pro подключён">
      <div className="pro-glow" />
      <div className="pro-top"><button className="pro-x" aria-label="Закрыть" onClick={() => onDone(false)}><Icon name="xmark" size={15} /></button></div>
      <div className="pro-body">
        <div className="pro-spark"><ProLockup size="xl" /></div>
        <h1 className="pro-h c">Ты в Pro</h1>
        <p className="pro-sub c">{until ? `До ${new Date(until).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).replace(' г.', '')}. ` : ''}Чек — на почте.</p>
        <div className="pro-chips">
          <span><Icon name="microphone" size={13} /> Голосом</span><span><Icon name="receipt" size={13} /> Чеки</span>
          <span><Icon name={ASK_ICON} size={13} /> Спроси SKOKA</span><span><Icon name="chart-pie" size={13} /> Разбор периода</span>
        </div>
        {offer && (
          <label className="pro-addpay">
            <div><b>Добавить в платежи</b>
              <span>{Math.round(offer.amount / 100).toLocaleString('ru-RU')} ₽ {every} · следующее {new Date(offer.next).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })} — «Можно тратить» учтёт его заранее</span></div>
            <input type="checkbox" checked={add} onChange={(e) => setAdd(e.target.checked)} />
            <span className="swbox" aria-hidden="true" />
          </label>
        )}
      </div>
      <div className="pro-cta"><button className="pro-btn" onClick={() => onDone(Boolean(offer) && add)}>{next}</button></div>
    </div>
  );
}

/** Оплата не прошла или ещё не подтверждена — без паники и с понятным следующим шагом. */
export function ProPayWait({ state, reason, onRetry, onClose }: {
  state: 'pending' | 'canceled'; reason: string | null; onRetry: () => void; onClose: () => void;
}) {
  return (
    <div className="pro-screen" role="dialog" aria-modal="true" aria-label="Оплата">
      <div className="pro-glow" />
      <div className="pro-top"><button className="pro-x" aria-label="Закрыть" onClick={onClose}><Icon name="xmark" size={15} /></button></div>
      <div className="pro-body">
        <ProLockup />
        <h1 className="pro-h s">{state === 'pending' ? 'Ждём подтверждение банка' : 'Оплата не прошла'}</h1>
        <p className="pro-sub">{state === 'pending'
          ? 'Обычно это несколько секунд. Pro включится сам, как только банк подтвердит оплату, — экран можно закрыть.'
          : `${reason ? `Причина: ${reason}. ` : ''}Деньги не списаны. Можно попробовать ещё раз или другой картой.`}</p>
      </div>
      <div className="pro-cta">
        {state === 'canceled' && <button className="pro-btn" onClick={onRetry}>Попробовать ещё раз</button>}
        <button className="pro-ghost" onClick={onClose}>{state === 'pending' ? 'Закрыть' : 'Не сейчас'}</button>
      </div>
    </div>
  );
}
